import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { dashboardSummary, weeklyDigest } from "@/lib/weave";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowRight, Sparkles, Compass, Layers, Calendar, MessageCircle } from "lucide-react";

const QUICK_LINKS = [
  { to: "/app/foundations", label: "Set your foundations", icon: Compass, color: "var(--teal)" },
  { to: "/app/pillars", label: "Shape your pillars", icon: Layers, color: "var(--yellow-deep)" },
  { to: "/app/calendar", label: "Draft this week", icon: Calendar, color: "var(--red)" },
  { to: "/app/coach", label: "Talk to your coach", icon: MessageCircle, color: "var(--teal-deep)" },
];

export default function MemberOverview() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [digest, setDigest] = useState(null);
  const [loadingDigest, setLoadingDigest] = useState(false);

  useEffect(() => {
    dashboardSummary().then(setSummary).catch(() => {});
  }, []);

  const generateDigest = async () => {
    setLoadingDigest(true);
    try {
      const d = await weeklyDigest();
      setDigest(d);
      if (!d.ready) toast.message(d.reason || "Add your foundations first.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate digest.");
    } finally {
      setLoadingDigest(false);
    }
  };

  if (!summary) {
    return <div className="overline">Loading workspace…</div>;
  }

  const c = summary.counts;

  return (
    <div data-testid="member-overview" className="space-y-12">
      <div>
        <div className="overline">The Weave · Workspace</div>
        <h1 className="font-display text-4xl md:text-6xl tracking-tight mt-3">
          Welcome back, <span className="text-[var(--teal)]">{(summary.user.name || "founder").split(" ")[0]}</span>.
        </h1>
        <p className="mt-3 text-[var(--ink-2)] max-w-xl">
          {summary.has_mission
            ? "Pick up where you left off. Your coach has been thinking about your next moves."
            : "Start with your mission — a single sentence that sets the direction for everything else."}
        </p>
      </div>

      {/* completeness */}
      <div className="card-tech rounded-sm p-6">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="overline">Brand readiness</div>
            <div className="font-display text-5xl mt-1">{summary.completeness}%</div>
          </div>
          <div className="text-sm text-[var(--ink-2)] max-w-md">
            We track this from four foundations: mission, strengths/values, content pillars, and your first draft.
          </div>
        </div>
        <div className="bar-track mt-4">
          <div
            className="bar-fill"
            style={{
              width: `${summary.completeness}%`,
              background:
                summary.completeness >= 75
                  ? "var(--teal)"
                  : summary.completeness >= 50
                  ? "var(--yellow-deep)"
                  : "var(--red)",
            }}
          />
        </div>
      </div>

      {/* counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          ["Pillars", c.pillars, "var(--teal)"],
          ["Drafts", c.drafts, "var(--yellow-deep)"],
          ["Scheduled", c.scheduled, "var(--red)"],
          ["Audits saved", c.audits, "var(--ink)"],
        ].map(([label, n, color]) => (
          <div key={label} className="card-tech rounded-sm p-5">
            <div className="overline">{label}</div>
            <div className="font-display text-4xl mt-1" style={{ color }}>
              {n}
            </div>
          </div>
        ))}
      </div>

      {/* digest */}
      <section>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="overline">Weekly digest</div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight mt-2">
              What to post <span className="text-[var(--teal)]">this week.</span>
            </h2>
          </div>
          <Button
            data-testid="digest-generate-btn"
            onClick={generateDigest}
            disabled={loadingDigest}
            className="btn-accent rounded-full h-11 px-5 flex items-center gap-2"
          >
            <Sparkles size={16} /> {loadingDigest ? "Thinking…" : "Generate digest"}
          </Button>
        </div>

        {digest?.ready ? (
          <div className="mt-6">
            <p className="text-lg text-[var(--ink-2)]">{digest.headline}</p>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              {(digest.ideas || []).map((i, idx) => (
                <div key={idx} className="card-tech rounded-sm p-5" data-testid={`digest-idea-${idx}`}>
                  <div className="overline">{i.day} · {i.pillar}</div>
                  <div className="font-display text-lg mt-2 leading-snug">{i.hook}</div>
                  <p className="text-sm text-[var(--ink-2)] mt-2">{i.angle}</p>
                </div>
              ))}
            </div>
            {digest.challenge && (
              <div
                className="mt-6 rounded-sm p-5 border-l-4"
                style={{ borderColor: "var(--red)", background: "var(--bg-2)" }}
              >
                <div className="overline" style={{ color: "var(--red)" }}>
                  This week's challenge
                </div>
                <p className="mt-2">{digest.challenge}</p>
              </div>
            )}
          </div>
        ) : digest && !digest.ready ? (
          <div className="mt-6 text-[var(--ink-2)]">{digest.reason}</div>
        ) : (
          <p className="mt-4 text-sm text-[var(--muted)]">
            Generate a Weave-voiced 3-post plan for the week, with hooks and angles.
          </p>
        )}
      </section>

      {/* quick actions */}
      <section>
        <div className="overline mb-4">Where to next</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {QUICK_LINKS.map((q) => (
            <button
              key={q.to}
              onClick={() => navigate(q.to)}
              data-testid={`quick-${q.label.replace(/\s+/g, "-").toLowerCase()}`}
              className="card-tech rounded-sm p-5 text-left flex flex-col items-start gap-3 group"
            >
              <q.icon size={20} style={{ color: q.color }} />
              <div className="font-display text-lg leading-tight">{q.label}</div>
              <ArrowRight size={16} className="text-[var(--muted)] group-hover:translate-x-1 transition-transform" />
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
